import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Complaint, ComplaintCategory, ComplaintOutcome, RoutingDetermination, RoutingSuggestion } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, Modal, PageHeader, StatCard, StatusBadge } from '../../ui/components';

const OMBUDSMAN_ROLES = ['OMBUDSMAN_OFFICER'];
const ROUTINGS: RoutingDetermination[] = ['OMBUDSMAN', 'ACCS', 'MDA_INTERNAL', 'OUT_OF_JURISDICTION'];
const CATEGORIES: ComplaintCategory[] = [
  'MALADMINISTRATION',
  'UNREASONABLE_DELAY',
  'DISCOURTESY',
  'UNFAIR_TREATMENT',
  'DENIAL_OF_SERVICE',
  'PROCUREMENT_IRREGULARITY',
  'CORRUPTION_SUSPECTED',
  'OTHER',
];
const OUTCOMES: ComplaintOutcome[] = ['UPHELD', 'PARTIALLY_UPHELD', 'NOT_UPHELD', 'RESOLVED_INFORMALLY', 'DISCONTINUED'];

type ModalKind = 'triage' | 'mda-response' | 'await' | 'resume' | 'escalate' | 'resolve' | 'withdraw' | null;

export function ComplaintDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('OMBUDSMAN_OFFICER') ?? false;
  const complaintQ = useApiQuery<Complaint>(id ? `/api/v1/m40/complaints/${id}` : null);
  const suggestionQ = useApiQuery<RoutingSuggestion>(isOfficer && complaintQ.data?.state === 'RECEIVED' && id ? `/api/v1/m40/complaints/${id}/routing-suggestion` : null);
  const [modal, setModal] = useState<ModalKind>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (complaintQ.loading) return <div className="px-4 py-8 text-sm text-slate-500">Loading complaint…</div>;
  if (complaintQ.error) return <ErrorNote message={complaintQ.error} />;
  const c = complaintQ.data;
  if (!c) return null;

  const isSelf = (persona?.nin && persona.nin === c.complainantNin) ?? false;
  const canWithdraw = isOfficer || isSelf;
  const post = async (path: string, body?: unknown) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/m40/complaints/${c.id}/${path}`, body);
      setModal(null);
      complaintQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${path} failed`);
    }
  };

  return (
    <div>
      <PageHeader title={c.refNo} subtitle={`${c.subjectMda} · ${c.category.replaceAll('_', ' ')}`} actions={<StatusBadge status={c.state} />} />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Channel" value={c.channel.replaceAll('_', ' ')} />
        <StatCard label="Routing" value={c.routingDetermination?.replaceAll('_', ' ') ?? 'Not yet triaged'} />
        <StatCard label="MDA response due" value={formatDateTime(c.mdaResponseDueAt)} tone={c.mdaResponseDueAt && !c.mdaRespondedAt && new Date(c.mdaResponseDueAt) < new Date() ? 'bad' : 'default'} />
        <StatCard label="Filed" value={formatDateTime(c.createdAt)} />
      </div>

      <div className="card mb-6 p-5">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Narrative</h2>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{c.narrative}</p>
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-slate-500 sm:grid-cols-3">
          <div>Complainant: {c.complainantName}</div>
          <div>Phone: {c.complainantPhone ?? '—'}</div>
          <div>Email: {c.complainantEmail ?? '—'}</div>
        </div>
        {c.signpostNote ? <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">Signpost: {c.signpostNote}</div> : null}
        {c.mdaResponseSummary ? <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50 p-2 text-xs text-sky-800">MDA response: {c.mdaResponseSummary}</div> : null}
        {c.outcome ? (
          <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
            Outcome: <StatusBadge status={c.outcome} /> — {c.outcomeNote}
          </div>
        ) : null}
        {c.withdrawnReason ? <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">Withdrawn: {c.withdrawnReason}</div> : null}
      </div>

      {isOfficer && c.state === 'RECEIVED' && suggestionQ.data ? (
        <div className="card mb-6 border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-800">
          Suggested routing (non-binding): <span className="font-bold">{suggestionQ.data.suggested.replaceAll('_', ' ')}</span> — {suggestionQ.data.reason}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {isOfficer && c.state === 'RECEIVED' ? (
          <button className="btn-primary" onClick={() => setModal('triage')}>
            Triage
          </button>
        ) : null}
        {isOfficer && c.state === 'REFERRED_TO_MDA' ? (
          <button className="btn-secondary" onClick={() => setModal('mda-response')}>
            Record MDA response
          </button>
        ) : null}
        {isOfficer && (['REFERRED_TO_MDA', 'UNDER_INVESTIGATION'] as string[]).includes(c.state) ? (
          <>
            <button className="btn-secondary" onClick={() => setModal('await')}>
              Await complainant
            </button>
            <button className="btn-secondary" onClick={() => setModal('escalate')}>
              Escalate to ACCS
            </button>
            <button className="btn-success" onClick={() => setModal('resolve')}>
              Resolve
            </button>
          </>
        ) : null}
        {isOfficer && c.state === 'AWAITING_COMPLAINANT' ? (
          <button className="btn-primary" onClick={() => setModal('resume')}>
            Resume
          </button>
        ) : null}
        {isOfficer && c.state === 'RESOLVED' ? (
          <button className="btn-secondary" onClick={() => post('close')}>
            Close
          </button>
        ) : null}
        {canWithdraw && (['RECEIVED', 'REFERRED_TO_MDA', 'UNDER_INVESTIGATION', 'AWAITING_COMPLAINANT'] as string[]).includes(c.state) ? (
          <button className="btn-danger" onClick={() => setModal('withdraw')}>
            Withdraw
          </button>
        ) : null}
      </div>
      {c.state === 'REFERRED_TO_ACCS' ? (
        <p className="mt-3 text-xs text-slate-400">Now handled from the ACCS side — see Integrity Cases → Referrals inbox.</p>
      ) : null}

      {modal === 'triage' ? (
        <TriageModal onClose={() => setModal(null)} onSubmit={(body) => post('triage', body)} />
      ) : modal === 'mda-response' ? (
        <TextModal title="Record MDA response" fieldLabel="Summary" onClose={() => setModal(null)} onSubmit={(v) => post('mda-response', { summary: v })} />
      ) : modal === 'await' ? (
        <TextModal title="Move to awaiting complainant" fieldLabel="Note" onClose={() => setModal(null)} onSubmit={(v) => post('await-complainant', { note: v })} />
      ) : modal === 'resume' ? (
        <ResumeModal onClose={() => setModal(null)} onSubmit={(to) => post('resume', { to })} />
      ) : modal === 'escalate' ? (
        <TextModal title="Escalate to ACCS" fieldLabel="Reason" onClose={() => setModal(null)} onSubmit={(v) => post('escalate-accs', { reason: v })} />
      ) : modal === 'resolve' ? (
        <ResolveModal onClose={() => setModal(null)} onSubmit={(body) => post('resolve', body)} />
      ) : modal === 'withdraw' ? (
        <TextModal title="Withdraw complaint" danger fieldLabel="Reason" onClose={() => setModal(null)} onSubmit={(v) => post('withdraw', { reason: v })} />
      ) : null}
    </div>
  );
}

function TriageModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (body: { routingDetermination: RoutingDetermination; category?: ComplaintCategory; signpostNote?: string }) => Promise<void> }) {
  const [routingDetermination, setRoutingDetermination] = useState<RoutingDetermination>('MDA_INTERNAL');
  const [category, setCategory] = useState<ComplaintCategory | ''>('');
  const [signpostNote, setSignpostNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ routingDetermination, category: category || undefined, signpostNote: signpostNote || undefined });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Triage failed');
      setBusy(false);
    }
  };

  return (
    <Modal title="Triage complaint" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Routing determination">
          <select className="field-input" value={routingDetermination} onChange={(e) => setRoutingDetermination(e.target.value as RoutingDetermination)}>
            {ROUTINGS.map((r) => (
              <option key={r} value={r}>
                {r.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Recategorise (optional)">
          <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as ComplaintCategory | '')}>
            <option value="">Keep current category</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        {routingDetermination === 'OUT_OF_JURISDICTION' ? (
          <Field label="Signpost note (required — where should the complainant actually go?)">
            <input className="field-input" value={signpostNote} onChange={(e) => setSignpostNote(e.target.value)} />
          </Field>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || (routingDetermination === 'OUT_OF_JURISDICTION' && signpostNote.trim().length < 5)}>
            {busy ? 'Triaging…' : 'Confirm triage'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ResumeModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (to: 'REFERRED_TO_MDA' | 'UNDER_INVESTIGATION') => Promise<void> }) {
  const [to, setTo] = useState<'REFERRED_TO_MDA' | 'UNDER_INVESTIGATION'>('UNDER_INVESTIGATION');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(to);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Resume failed');
      setBusy(false);
    }
  };
  return (
    <Modal title="Resume complaint handling" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Resume to">
          <select className="field-input" value={to} onChange={(e) => setTo(e.target.value as typeof to)}>
            <option value="REFERRED_TO_MDA">Referred to MDA</option>
            <option value="UNDER_INVESTIGATION">Under investigation</option>
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Resuming…' : 'Resume'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ResolveModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (body: { outcome: ComplaintOutcome; outcomeNote: string }) => Promise<void> }) {
  const [outcome, setOutcome] = useState<ComplaintOutcome>('UPHELD');
  const [outcomeNote, setOutcomeNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ outcome, outcomeNote });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Resolution failed');
      setBusy(false);
    }
  };
  return (
    <Modal title="Resolve complaint" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Outcome">
          <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value as ComplaintOutcome)}>
            {OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {o.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Outcome note">
          <textarea className="field-input" rows={3} value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-success" onClick={submit} disabled={busy || outcomeNote.trim().length < 3}>
            {busy ? 'Resolving…' : 'Resolve'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function TextModal({
  title,
  fieldLabel,
  danger,
  onClose,
  onSubmit,
}: {
  title: string;
  fieldLabel: string;
  danger?: boolean;
  onClose: () => void;
  onSubmit: (value: string) => Promise<void>;
}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(value);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
      setBusy(false);
    }
  };
  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label={fieldLabel}>
          <textarea className="field-input" rows={3} value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className={danger ? 'btn-danger' : 'btn-primary'} onClick={submit} disabled={busy || value.trim().length < 3}>
            {busy ? 'Working…' : 'Confirm'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
